import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CalendarioRecebimentoDia, CompraMpResponse, StatusEntregaCompra, StatusPagamentoCompra } from '@triade/contracts';
import { ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { useCalendarioRecebimento, useCompras, useExcluirCompra } from '@/api/plano';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, TAMANHOS_PAGINA, TAMANHO_PAGINA_PADRAO } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarData, formatarMoeda, formatarQuantidade } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { BotaoRelatorios } from '@/relatorios';
import { type AbaCompras, deslocarMes, ENTREGAS_ORDEM, PAGAMENTOS_ORDEM, ROTULO_ENTREGA, ROTULO_PAGAMENTO, rotuloMes } from './compras-busca';
import { CompraDialog } from './compra-dialog';
import { UsaAlimenta } from './usa-alimenta';

export { ABAS_COMPRAS, lerBuscaCompras, type AbaCompras } from './compras-busca';

const CAMPO_FILTRO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const BOTAO_MES = 'flex size-[34px] items-center justify-center rounded-[9px] border border-border bg-card hover:bg-line-soft';
const BOTAO_ACAO = 'flex size-[30px] items-center justify-center rounded-[8px] text-ink-secondary hover:bg-line-soft hover:text-ink-body';

/**
 * Compras de materia-prima (/plano/compras): lista do mes e calendario de recebimento por semana.
 * Nenhum valor e calculado aqui: R$, totais e somas vem prontos da API.
 */
export function ComprasPage({ mes, aba = 'compras' }: { mes: string; aba?: AbaCompras }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const mudar = (patch: { mes?: string; aba?: AbaCompras }) => void navigate({ to: '/plano/compras', search: { mes, aba, ...patch }, replace: true });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Compras de MP
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Compras de MP</h1>
          <p className="text-small text-ink-secondary">Compras de matéria-prima por entrega e pagamento, e calendário de recebimento</p>
          <UsaAlimenta
            usa={[
              { rotulo: 'Necessidade de MP', to: '/plano/necessidade' },
              { rotulo: 'Fornecedores', to: '/cadastros/fornecedores' },
            ]}
            alimenta={[{ rotulo: 'Fluxo de caixa', to: '/plano/fluxo' }]}
          />
        </div>
        <nav aria-label="Mês das compras" className="flex items-center gap-2">
          <button type="button" aria-label="Mês anterior" className={BOTAO_MES} onClick={() => mudar({ mes: deslocarMes(mes, -1) })}>
            <ChevronLeft aria-hidden="true" className="size-4" />
          </button>
          <span aria-live="polite" className="min-w-[90px] text-center text-body font-semibold tabular-nums" data-testid="mes-compras">
            {rotuloMes(mes)}
          </span>
          <button type="button" aria-label="Próximo mês" className={BOTAO_MES} onClick={() => mudar({ mes: deslocarMes(mes, 1) })}>
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        </nav>
      </div>

      {empresaId === null ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver as compras.</p>
      ) : (
        <Tabs value={aba} onValueChange={(v) => mudar({ aba: v as AbaCompras })} className="gap-4">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="compras">Compras</TabsTrigger>
            <TabsTrigger value="recebimento">Recebimento</TabsTrigger>
          </TabsList>
          <TabsContent value="compras">
            <AbaListaCompras mes={mes} />
          </TabsContent>
          <TabsContent value="recebimento">
            <AbaRecebimento mes={mes} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ---------- Aba Compras ----------

function AbaListaCompras({ mes }: { mes: string }) {
  const { pode } = usePode();
  const podeEditar = pode('plano.editar');
  const [statusEntrega, setStatusEntrega] = useState<StatusEntregaCompra | ''>('');
  const [statusPagamento, setStatusPagamento] = useState<StatusPagamentoCompra | ''>('');
  // Paginacao do servidor (S36-D3): trocar filtro ou mes volta para a pagina 1.
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState<number>(TAMANHO_PAGINA_PADRAO);
  const lista = useCompras({
    mes,
    limit: tamanho,
    offset: (pagina - 1) * tamanho,
    ...(statusEntrega ? { statusEntrega } : {}),
    ...(statusPagamento ? { statusPagamento } : {}),
  });
  useEffect(() => setPagina(1), [mes, statusEntrega, statusPagamento]);
  const excluir = useExcluirCompra();
  const [editando, setEditando] = useState<CompraMpResponse | 'novo' | null>(null);
  const [excluindo, setExcluindo] = useState<CompraMpResponse | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);
  const compras = lista.data?.itens ?? [];
  const filtrado = Boolean(statusEntrega || statusPagamento);

  async function confirmarExclusao() {
    if (!excluindo) return;
    setErroExclusao(null);
    try {
      await excluir.mutateAsync({ id: excluindo.id });
      setExcluindo(null);
    } catch (e) {
      setErroExclusao(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
    }
  }

  let corpo: ReactNode;
  if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as compras" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando compras…</Mensagem>;
  else if (compras.length === 0) corpo = <Mensagem papel="status">{filtrado ? 'Nenhuma compra com esses filtros.' : 'Nenhuma compra neste mês. Lance as compras de matéria-prima que cobrem a Necessidade de MP.'}</Mensagem>;
  else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] border-collapse text-small" aria-label="Compras de matéria-prima do mês">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Pedido</th>
              <th scope="col" className="px-3 py-2.5 font-medium">NF</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Emissão</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Fornecedor</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Produto</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Qtd (t)</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Preço</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Total</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Entrega</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Pagamento</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Status entrega</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Status pgto</th>
              {podeEditar && <th scope="col" className="px-3 py-2.5 font-medium"><span className="sr-only">Ações</span></th>}
            </tr>
          </thead>
          <tbody>
            {compras.map((c) => (
              <tr key={c.id} data-testid="linha-compra" className="border-t border-line-soft">
                <td className="px-4 py-3 font-mono font-semibold">{c.numeroPedido ?? '—'}</td>
                <td className="px-3 py-3">{c.nf ?? '—'}</td>
                <td className="px-3 py-3 tabular-nums">{formatarData(c.emissao)}</td>
                <td className="px-3 py-3">{c.fornecedorNome ?? '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">
                  <span className="font-mono">{c.itemCodigo}</span> {c.itemDescricao}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidade(c.quantidade)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(c.precoUnitario)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(c.total)}</td>
                <td className="px-3 py-3 tabular-nums">{formatarData(c.dataEntrega)}</td>
                <td className="px-3 py-3 tabular-nums">{formatarData(c.dataPagamento)}</td>
                <td className="px-3 py-3">
                  <BadgeEntrega status={c.statusEntrega} />
                </td>
                <td className="px-3 py-3">
                  <BadgePagamento status={c.statusPagamento} />
                </td>
                {podeEditar && (
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" aria-label={`Editar compra ${c.numeroPedido ?? c.itemCodigo}`} className={BOTAO_ACAO} onClick={() => setEditando(c)}>
                        <Pencil aria-hidden="true" className="size-4" />
                      </button>
                      <button type="button" aria-label={`Excluir compra ${c.numeroPedido ?? c.itemCodigo}`} className={cn(BOTAO_ACAO, 'hover:text-danger-ink')} onClick={() => { setErroExclusao(null); setExcluindo(c); }}>
                        <Trash2 aria-hidden="true" className="size-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-label="Compras do mês" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="compra-entrega-filtro" className="text-caption font-medium text-ink-secondary">Entrega</label>
          <select id="compra-entrega-filtro" className={CAMPO_FILTRO} value={statusEntrega} onChange={(e) => setStatusEntrega(e.target.value as StatusEntregaCompra | '')}>
            <option value="">Todas</option>
            {ENTREGAS_ORDEM.map((s) => (
              <option key={s} value={s}>
                {ROTULO_ENTREGA[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="compra-pagamento-filtro" className="text-caption font-medium text-ink-secondary">Pagamento</label>
          <select id="compra-pagamento-filtro" className={CAMPO_FILTRO} value={statusPagamento} onChange={(e) => setStatusPagamento(e.target.value as StatusPagamentoCompra | '')}>
            <option value="">Todos</option>
            {PAGAMENTOS_ORDEM.map((s) => (
              <option key={s} value={s}>
                {ROTULO_PAGAMENTO[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {corpo}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-compras">
        {lista.data ? (
          <>
            <span>
              {formatarContagem(lista.data.total)} {lista.data.total === 1 ? 'compra' : 'compras'}
              {lista.data.total > compras.length ? ` (mostrando ${formatarContagem(compras.length)})` : ''}
            </span>
            <span className="tabular-nums" data-testid="soma-compras">
              Total no filtro: {formatarQuantidade(lista.data.somaQuantidade)} t · {formatarMoeda(lista.data.somaTotal)}
            </span>
          </>
        ) : (
          ' '
        )}
      </div>

      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Nova compra', onClick: () => setEditando('novo') } : undefined}
        paginacao={
          lista.data
            ? { pagina, tamanho, total: lista.data.total, onPagina: setPagina, onTamanho: (t) => { setTamanho(t); setPagina(1); }, tamanhos: [...TAMANHOS_PAGINA] }
            : undefined
        }
        relatorios={<BotaoRelatorios tela="plano-compras" params={{ mes, statusEntrega: statusEntrega || undefined, statusPagamento: statusPagamento || undefined }} />}
      />

      {editando !== null && <CompraDialog key={editando === 'novo' ? 'novo' : editando.id} compra={editando === 'novo' ? null : editando} mes={mes} onFechar={() => setEditando(null)} />}
      <AlertDialog open={excluindo !== null} onOpenChange={(o) => !o && !excluir.isPending && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir compra {excluindo?.numeroPedido ?? excluindo?.itemCodigo}?</AlertDialogTitle>
            <AlertDialogDescription>A compra sai da lista, da necessidade de MP e do fluxo de caixa deste mês.</AlertDialogDescription>
          </AlertDialogHeader>
          {erroExclusao && (
            <p role="alert" className="text-small text-danger-ink">
              Não foi possível excluir: {erroExclusao}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluir.isPending}
              onClick={(e) => {
                e.preventDefault();
                void confirmarExclusao();
              }}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ---------- Aba Recebimento ----------

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function AbaRecebimento({ mes }: { mes: string }) {
  const calendario = useCalendarioRecebimento(mes);

  if (calendario.isError && !calendario.data) return <div className="p-4"><ErroLista erro={calendario.error} onTentar={() => void calendario.refetch()} titulo="Não foi possível carregar o recebimento" /></div>;
  if (!calendario.data) return <Mensagem papel="status">Carregando recebimento…</Mensagem>;
  const semanas = calendario.data.semanas;
  if (semanas.length === 0) return <Mensagem papel="status">Nenhuma entrega de MP neste mês.</Mensagem>;

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full min-w-[900px] table-fixed border-collapse text-small" aria-label="Calendário de recebimento de MP por semana">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="w-[90px] px-3 py-2.5 font-medium">Semana</th>
            {DIAS_SEMANA.map((d) => (
              <th key={d} scope="col" className="px-2 py-2.5 font-medium">{d}</th>
            ))}
            <th scope="col" className="w-[130px] px-3 py-2.5 text-right font-medium">Total da semana</th>
          </tr>
        </thead>
        <tbody>
          {semanas.map((s) => (
            <tr key={s.semana} className="border-t border-line-soft align-top">
              <th scope="row" className="px-3 py-3 text-left font-semibold">Semana {s.semana}</th>
              {DIAS_SEMANA.map((_, i) => {
                const dia = s.dias.find((d) => d.diaSemana === i + 1);
                return (
                  <td key={i} className="px-1.5 py-2">
                    {dia && <CelulaRecebimento dia={dia} />}
                  </td>
                );
              })}
              <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatarQuantidade(s.quantidade)} t</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CelulaRecebimento({ dia }: { dia: CalendarioRecebimentoDia }) {
  return (
    <div className="flex min-h-[64px] flex-col gap-1.5 rounded-lg bg-surface-muted/50 p-1.5">
      <div className="flex items-center justify-between text-caption text-ink-muted">
        <span className="tabular-nums">{formatarData(dia.data)}</span>
        <span className="font-semibold tabular-nums">{formatarQuantidade(dia.quantidade)} t</span>
      </div>
      {dia.compras.map((c) => (
        <div key={c.id} data-testid="chip-recebimento" className="flex flex-col gap-0.5 rounded-md border border-border bg-card px-1.5 py-1 text-caption">
          <span className="font-medium">{c.fornecedorNome ?? 'Sem fornecedor'}</span>
          <span className="text-ink-secondary">
            {c.itemCodigo} · {formatarQuantidade(c.quantidade)} t
          </span>
          <BadgeEntrega status={c.statusEntrega} />
        </div>
      ))}
    </div>
  );
}

// ---------- Comuns ----------

const CLASSE_ENTREGA: Record<StatusEntregaCompra, string> = {
  A_ENTREGAR: 'bg-warning-bg text-warning-ink',
  PREVISTO: 'bg-line-soft text-ink-body',
  ENTREGUE: 'bg-success-bg text-success-ink',
  REPROGRAMAR: 'bg-danger-bg text-danger-ink',
};

const CLASSE_PAGAMENTO: Record<StatusPagamentoCompra, string> = {
  A_PAGAR: 'bg-warning-bg text-warning-ink',
  PAGO: 'bg-success-bg text-success-ink',
};

function BadgeEntrega({ status }: { status: StatusEntregaCompra }) {
  return <span className={cn('inline-block rounded-full px-2 py-0.5 text-caption font-semibold whitespace-nowrap', CLASSE_ENTREGA[status])}>{ROTULO_ENTREGA[status]}</span>;
}

function BadgePagamento({ status }: { status: StatusPagamentoCompra }) {
  return <span className={cn('inline-block rounded-full px-2 py-0.5 text-caption font-semibold whitespace-nowrap', CLASSE_PAGAMENTO[status])}>{ROTULO_PAGAMENTO[status]}</span>;
}

function Mensagem({ children, papel }: { children: ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
